import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import FilterIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { WorkOrderConditionFlowStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { StringToListMatchType } from "@redotech/redo-model/advanced-flow/common/condition";
import {
  BaseWhereStep,
  getConditionTypeLabel,
  OmsConditionType,
  OmsLineItemCustomAttributeConditionStep,
  OmsProductWhereCondition,
} from "@redotech/redo-model/advanced-flow/oms/conditions";
import { OmsConditionStep } from "@redotech/redo-model/advanced-flow/steps";
import {
  workOrderConditionToWhereConditionTypes,
  WorkOrderProductConditionTypes,
} from "@redotech/redo-model/advanced-flow/work-order/conditions";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { Text } from "@redotech/redo-web/text";
import Fuse from "fuse.js";
import { produce } from "immer";
import React, { Fragment, memo, useMemo, useState } from "react";
import {
  DeepPartial,
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "..";
import { useAvailableSteps } from "../hooks/use-available-steps";
import { OmsNumberMatchTypeSelect } from "../oms/number-match-type-select";
import { OmsListMatchValuesInput } from "../oms/oms-list-match-values-input";
import { OmsListToListMatchTypeSelect } from "../oms/oms-list-to-list-match-type-select";
import { OmsRequireExactMatchInput } from "../oms/oms-require-exact-match-input";
import { OmsSingleNumberStepInput } from "../oms/single-number-step-input";
import { StringToListMatchTypeSelect } from "../oms/string-to-list-match-type-select";

function createOptionsFetcher(
  filterName: string,
  rpcClient: NonNullable<
    React.ContextType<typeof RedoOutboundLabelsRpcClientContext>
  >,
) {
  return async ({ searchText }: { searchText: string }) => {
    const { values } = await rpcClient.getFulfillmentGroupUniqueValues({
      advancedFiltersData: [],
      filterName: filterName as any,
      filterSearch: searchText,
    });
    return values;
  };
}

function getConditionFields(
  step: DeepPartial<OmsConditionStep>,
  setStep: (step: DeepPartial<OmsConditionStep>) => void,
  outboundLabelRpcClient: NonNullable<
    React.ContextType<typeof RedoOutboundLabelsRpcClientContext>
  >,
) {
  switch (step.conditionType) {
    case OmsConditionType.SKU:
      return [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="sku"
          optionsFetcher={createOptionsFetcher(
            "itemSkus",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search product SKUs..."
        />,
        <OmsRequireExactMatchInput key="exact" setStep={setStep} step={step} />,
      ];
    case OmsConditionType.PRODUCT_NAME:
      return [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="name"
          optionsFetcher={createOptionsFetcher(
            "productTitle",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search product names..."
        />,
        <OmsRequireExactMatchInput key="exact" setStep={setStep} step={step} />,
      ];
    case OmsConditionType.VARIANT_TITLE:
      return [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="variant"
          optionsFetcher={createOptionsFetcher(
            "productVariant",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search variant titles..."
        />,
        <OmsRequireExactMatchInput key="exact" setStep={setStep} step={step} />,
      ];
    case OmsConditionType.PRODUCT_TYPE:
      return [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="type"
          optionsFetcher={createOptionsFetcher(
            "productType",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search product types..."
        />,
        <OmsRequireExactMatchInput key="exact" setStep={setStep} step={step} />,
      ];
    case OmsConditionType.PRODUCT_TAG:
      return [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="tag"
          optionsFetcher={createOptionsFetcher(
            "shopifyProductTags",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search product tags..."
        />,
        <OmsRequireExactMatchInput key="exact" setStep={setStep} step={step} />,
      ];
    case OmsConditionType.ITEM_QUANTITY:
      return [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer
            money={false}
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
    case OmsConditionType.VENDOR:
      return [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="vendor"
          optionsFetcher={createOptionsFetcher(
            "vendors",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder="Search vendors..."
        />,
        <OmsRequireExactMatchInput key="exact" setStep={setStep} step={step} />,
      ];
    case OmsConditionType.ITEM_COST:
      return [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer={false}
            money
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
    case OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE: {
      const isExists =
        (step as DeepPartial<OmsLineItemCustomAttributeConditionStep>)
          .matchType === StringToListMatchType.EXISTS;
      return [
        <RedoTextInput
          key="key"
          placeholder="Attribute key"
          setValue={(value) => setStep({ ...step, matchKey: value })}
          size="sm"
          value={
            (step as DeepPartial<OmsLineItemCustomAttributeConditionStep>)
              .matchKey ?? ""
          }
        />,
        <StringToListMatchTypeSelect
          key="match"
          setStep={setStep}
          step={step}
        />,
        ...(!isExists
          ? [
              <OmsListMatchValuesInput
                addTagText="Add value"
                key="values"
                optionsFetcher={() => Promise.resolve([])}
                setStep={setStep}
                step={
                  step as PartialWithRequiredType<OmsLineItemCustomAttributeConditionStep>
                }
                tagInputPlaceholder="Enter attribute values..."
              />,
            ]
          : []),
      ];
    }
    case undefined:
      return [];
    default:
      return [];
  }
}

export const ConditionFields = memo(function ConditionFields({
  conditionTypeOptions,
  step,
  setStep,
  setConditionType,
  index = 0,
}: {
  conditionTypeOptions?: OmsConditionType[];
  step: DeepPartial<OmsConditionStep>;
  setStep: (step: DeepPartial<OmsConditionStep>, index: number) => void;
  setConditionType: (
    item: RedoListItem<OmsConditionType>,
    index: number,
  ) => void;
  index?: number;
}) {
  const outboundLabelRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const [searchString, setSearchString] = useState("");

  const setStepFields = useHandler((newStep: DeepPartial<OmsConditionStep>) => {
    setStep(newStep, index);
  });

  const fields = getConditionFields(
    step,
    setStepFields,
    outboundLabelRpcClient,
  );

  const allOptions = useMemo((): TextRedoListItem<OmsConditionType>[] => {
    const types = conditionTypeOptions ?? [...WorkOrderProductConditionTypes];
    return types
      .map((type) => ({
        value: type,
        type: "text" as const,
        id: type,
        text: getConditionTypeLabel(type),
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [conditionTypeOptions]);

  const searcher = useMemo(
    () =>
      new Fuse<TextRedoListItem<OmsConditionType>>([], {
        keys: ["text"],
        threshold: 0.3,
      }),
    [],
  );

  const filteredOptions = useSearch(searcher, allOptions, searchString);

  const optionSelected = useHandler(
    ({ item }: { item: RedoListItem<OmsConditionType> }) => {
      setConditionType(item, index);
    },
  );

  return (
    <>
      <RedoSingleSelectDropdownInput<OmsConditionType>
        fitToAnchor={false}
        flexGrow={false}
        options={filteredOptions}
        optionSelected={optionSelected}
        placeholder="Select a field"
        searchPlaceholder="Search conditions..."
        searchString={searchString}
        selectedItem={allOptions.find((o) => o.id === step.conditionType)}
        setSearchString={setSearchString}
        size="xs"
      />
      {fields.map((field, i) => (
        <Fragment key={i}>{field}</Fragment>
      ))}
    </>
  );
});

const WhereFilters = memo(function WhereFilters({
  clauses,
  step,
  setStep,
}: {
  clauses: DeepPartial<BaseWhereStep>[];
  step: PartialWithRequiredType<WorkOrderConditionFlowStep>;
  setStep: (step: PartialWithRequiredType<WorkOrderConditionFlowStep>) => void;
}) {
  const setSubStep = useHandler(
    (newStep: DeepPartial<OmsConditionStep>, index: number) => {
      if (step.where === undefined) {
        step.where = [];
      }
      if (index > step.where.length) {
        index = step.where.length;
      }
      step.where[index] = newStep;
      setStep(step);
    },
  );

  const setConditionType = useHandler(
    (item: RedoListItem<OmsConditionType>, index: number) => {
      setSubStep(
        { conditionType: item.value } as DeepPartial<OmsConditionStep>,
        index,
      );
    },
  );

  const onDeleteWhereClause = useHandler((index: number) => {
    step.where?.splice(index, 1);
    setStep(step);
  });

  return (
    <Flex dir="column" justify="flex-start">
      {clauses.map((clause, index) => (
        <Flex
          dir="column"
          gap="none"
          justify="flex-start"
          key={`where-${index}`}
        >
          <Flex align="center" justify="space-between" w="full">
            <Text fontSize="xs" fontWeight="medium">
              {index === 0 ? "Where" : "And"}
            </Text>
            <RedoButton
              IconLeading={() => <XIcon />}
              onClick={() => onDeleteWhereClause(index)}
            />
          </Flex>
          <Flex align="center" dir="row" gap="md" w="full" wrap="wrap">
            <ConditionFields
              conditionTypeOptions={
                step.conditionType
                  ? workOrderConditionToWhereConditionTypes[
                      step.conditionType as OmsConditionType
                    ]
                  : undefined
              }
              index={index}
              setConditionType={setConditionType}
              setStep={setSubStep}
              step={clause as DeepPartial<OmsConditionStep>}
            />
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
});

const WorkOrderConditionInspector = memo(function WorkOrderConditionInspector({
  step,
  setStep,
  onClose,
  steps,
  setHoveredNodeId,
}: StepTypeDetailsProps<PartialWithRequiredType<WorkOrderConditionFlowStep>>) {
  const trueStep = step.nextTrueId
    ? steps?.find((s) => s.id === step.nextTrueId)
    : undefined;
  const falseStep = step.nextFalseId
    ? steps?.find((s) => s.id === step.nextFalseId)
    : undefined;

  const { availableSteps, createConnectionHandler, onItemHover } =
    useAvailableSteps(step, steps ?? [], setStep, setHoveredNodeId, "End");

  const handleTrueStepSelected = createConnectionHandler(0);
  const handleFalseStepSelected = createConnectionHandler(1);
  const showFilterButton = useMemo(() => {
    if (!step.conditionType) {
      return false;
    }
    return (
      workOrderConditionToWhereConditionTypes[
        step.conditionType as OmsConditionType
      ].length > 0
    );
  }, [step]);

  const setConditionType = useHandler(
    (item: RedoListItem<OmsConditionType>) => {
      setStep({
        type: step.type,
        nextTrueId: step.nextTrueId,
        nextFalseId: step.nextFalseId,
        id: step.id,
        conditionType: item.value,
      } as PartialWithRequiredType<WorkOrderConditionFlowStep>);
    },
  );

  const setStepFields = useHandler((newStep: DeepPartial<OmsConditionStep>) => {
    setStep({
      ...newStep,
      type: step.type,
      nextTrueId: step.nextTrueId,
      nextFalseId: step.nextFalseId,
      id: step.id,
    } as PartialWithRequiredType<WorkOrderConditionFlowStep>);
  });

  const onClickFilter = useHandler(() => {
    const where = step.where ?? [];
    where.push({});
    setStepFields({
      ...step,
      where: where as DeepPartial<OmsProductWhereCondition>[],
    } as unknown as DeepPartial<OmsConditionStep>);
  });

  const setConditionTypeWrapper = useHandler(
    (item: RedoListItem<OmsConditionType>, _index: number) =>
      setConditionType(item),
  );

  const setStepWrapper = useHandler(
    (newStep: DeepPartial<OmsConditionStep>, _index: number) =>
      setStepFields(newStep),
  );

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="sm" fontWeight="semibold">
          Condition
        </Text>
        {onClose && (
          <div className={inspectorStyles.closeButton} onClick={onClose}>
            <XIcon height="16" width="16" />
          </div>
        )}
      </Flex>
      <Flex dir="column" gap="none">
        <Flex align="center" justify="space-between" w="full">
          <Text fontSize="xs" fontWeight="medium">
            If
          </Text>
          {showFilterButton && (
            <RedoButton
              IconLeading={() => <FilterIcon />}
              onClick={onClickFilter}
            />
          )}
        </Flex>
        <Flex align="center" dir="row" gap="md" w="full" wrap="wrap">
          <ConditionFields
            conditionTypeOptions={[...WorkOrderProductConditionTypes]}
            setConditionType={setConditionTypeWrapper}
            setStep={setStepWrapper}
            step={step as unknown as DeepPartial<OmsConditionStep>}
          />
        </Flex>
      </Flex>
      <WhereFilters clauses={step.where ?? []} setStep={setStep} step={step} />

      <Divider />

      <Flex dir="column" gap="lg">
        <Text fontSize="md" fontWeight="semibold">
          Next steps
        </Text>

        <RedoSingleSelectDropdownInput<string>
          fitToAnchor
          label="If condition matches (True)"
          onOptionMouseEnter={(item) => onItemHover(item.value)}
          onOptionMouseLeave={() => onItemHover(null)}
          options={availableSteps}
          optionSelected={({ item }) => handleTrueStepSelected(item.value)}
          placeholder="Select a step"
          selectedItem={
            trueStep
              ? (availableSteps.find((opt) => opt.value === trueStep.id) ?? {
                  id: trueStep.id,
                  type: "text" as const,
                  text: `Invalid Step (${trueStep.id})`,
                  value: trueStep.id,
                })
              : undefined
          }
        />

        <RedoSingleSelectDropdownInput<string>
          fitToAnchor
          label="If condition does not match (False)"
          onOptionMouseEnter={(item) => onItemHover(item.value)}
          onOptionMouseLeave={() => onItemHover(null)}
          options={availableSteps}
          optionSelected={({ item }) => handleFalseStepSelected(item.value)}
          placeholder="Select a step"
          selectedItem={
            falseStep
              ? (availableSteps.find((opt) => opt.value === falseStep.id) ?? {
                  id: falseStep.id,
                  type: "text" as const,
                  text: `Invalid Step (${falseStep.id})`,
                  value: falseStep.id,
                })
              : undefined
          }
        />
      </Flex>
    </Flex>
  );
});

export const workOrderConditionStepType: FrontendStepType<
  PartialWithRequiredType<WorkOrderConditionFlowStep>
> = {
  valid: (step) => !!step.conditionType,
  type: "custom",
  customInspector: WorkOrderConditionInspector,
  updateConnection: (state, handleIndex, targetStepId) => {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextTrueId = targetStepId;
      } else if (handleIndex === 1) {
        draft.nextFalseId = targetStepId;
      }
    });
  },
  removeConnection: (state, targetStepId) => {
    return produce(state, (draft) => {
      if (draft.nextTrueId === targetStepId) {
        draft.nextTrueId = undefined as any;
      }
      if (draft.nextFalseId === targetStepId) {
        draft.nextFalseId = undefined as any;
      }
    });
  },
};
